import { apiService } from '../../../components/api/apiService';

export const POST_WORKFLOW_PATH = '/api/post-workflow';

export function workflowApiErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: unknown; status?: number } }).response;
    const d = resp?.data;
    if (typeof d === 'string' && d.trim()) return d.trim();
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>;
      const m = o.message ?? o.title ?? o.detail ?? o.error;
      if (typeof m === 'string' && m.trim()) return m.trim();
    }
    if (resp?.status != null) return `Request failed (HTTP ${resp.status}).`;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Request failed. Please try again.';
}

/** Request body for POST /api/post-workflow and PUT /api/post-workflow/{id} */
export type PostWorkflowRequest = {
  schemaVersion: number;
  name: string;
  icon: string;
  description: string;
  notes: string;
  /** JSON string: graph + viewport (see buildWorkflowDefinitionString). */
  definition: string;
  /** JSON string: execution schema v1 (see serializeWorkflowExecutionV1). */
  workFlowJson: string;
  isActive: boolean;
  lastComments: string;
};

/** GET /api/post-workflow/{id} response (snake_case from API). */
export type WorkflowDetailDto = {
  id: string;
  tenant_id?: string;
  is_active?: boolean;
  created_by?: string;
  updated_by?: string;
  created_on?: string;
  updated_on?: string;
  last_comments?: string;
  schema_version?: number;
  name: string;
  icon?: string;
  description?: string;
  notes?: string;
  definition?: string;
  work_flow_json?: string;
};

export async function postWorkflow(payload: PostWorkflowRequest): Promise<unknown> {
  return apiService.post(POST_WORKFLOW_PATH, payload, 'backend');
}

export async function getWorkflowById(workflowId: string): Promise<WorkflowDetailDto> {
  const id = encodeURIComponent(workflowId.trim());
  return apiService.get(`${POST_WORKFLOW_PATH}/${id}`, {}, 'backend') as Promise<WorkflowDetailDto>;
}

export async function putWorkflow(workflowId: string, payload: PostWorkflowRequest): Promise<unknown> {
  const id = encodeURIComponent(workflowId.trim());
  return apiService.put(`${POST_WORKFLOW_PATH}/${id}`, payload, 'backend');
}

/** Row from GET /api/post-workflow/available?isActive= */
export type AvailableWorkflowDto = {
  id: string;
  name: string;
  description?: string;
  status?: string;
  icon?: string;
  created_on?: string;
  created_by?: string;
  errors_count?: number;
  successful_runs?: number;
};

function coerceAvailableWorkflowList(data: unknown): AvailableWorkflowDto[] {
  if (Array.isArray(data)) return data as AvailableWorkflowDto[];
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    for (const key of ['items', 'data', 'workflows', 'results'] as const) {
      const v = o[key];
      if (Array.isArray(v)) return v as AvailableWorkflowDto[];
    }
  }
  return [];
}

/** GET /api/post-workflow/available — lists saved workflows (uses REACT_APP_API_URL_backend). */
export async function fetchAvailableWorkflows(isActive: boolean): Promise<AvailableWorkflowDto[]> {
  const data = await apiService.get(`${POST_WORKFLOW_PATH}/available`, { isActive }, 'backend');
  return coerceAvailableWorkflowList(data);
}

/** GET /api/post-workflow/{id}/logs — run history (shape may vary by backend). */
export async function fetchWorkflowLogs(workflowId: string): Promise<unknown> {
  const id = encodeURIComponent(workflowId.trim());
  return apiService.get(`${POST_WORKFLOW_PATH}/${id}/logs`, {}, 'backend');
}
